import {
  DocxEditor,
  DOCX_MIME_TYPE,
  useDocxTemplateFieldActions,
  type DocxEditorHandle,
} from "@/components/features/matter/file-repo/DocxEditor";
import { TemplateFieldsDialog } from "@/components/features/settings/defaults/matter-templates/TemplateFieldsDialog";
import { ActionConfirmationModal, Button, SheetFooter } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useFileEditor } from "@/lib/data/react-query/files/use-file-editor";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import type { FirmFile } from "@/lib/types";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

interface EditDocxFirmFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sourceFile: FirmFile;
  onSaved?: () => void;
  showSaveAsCopyButton?: boolean;
  templateFieldScope?: "all" | "signers-only";
}

export const EditDocxFirmFileModal = ({
  open,
  setOpen,
  sourceFile,
  onSaved,
  showSaveAsCopyButton = true,
  templateFieldScope = "all",
}: EditDocxFirmFileModalProps) => {
  const editorFile = useFileEditor(sourceFile.id, open);
  const editorRef = useRef<DocxEditorHandle | null>(null);
  const {
    templateFieldsTriggerRef,
    handleInsertAtCursor,
    handleOpenTemplateFields,
  } = useDocxTemplateFieldActions(editorRef);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saveMode, setSaveMode] = useState<"copy" | "original" | null>(null);
  const displayName = useMemo(
    () => getFirmFileDisplayName(sourceFile),
    [sourceFile],
  );

  useEffect(() => {
    if (!open) {
      setIsEditorReady(false);
      setIsExporting(false);
      setConfirmClose(false);
      setSaveMode(null);
      return;
    }
  }, [displayName, open]);

  const handleCreateFromDocx = async () => {
    if (!editorRef.current) {
      toast.error("Document editor is not ready yet.");
      return;
    }

    setSaveMode("copy");
    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.copy.mutateAsync({
        blob,
        contentType: DOCX_MIME_TYPE,
        sourceName: displayName,
      });

      toast.success("Document saved as a copy.");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save edited document", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const handleSaveOriginal = async () => {
    if (!editorRef.current) {
      toast.error("Document editor is not ready yet.");
      return;
    }

    setSaveMode("original");
    setIsExporting(true);

    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.save.mutateAsync({ blob, contentType: DOCX_MIME_TYPE });

      toast.success("Document saved");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save document", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const isSaving =
    editorFile.copy.isPending ||
    editorFile.save.isPending ||
    isExporting ||
    saveMode !== null;
  const allowedTemplateFieldParents =
    templateFieldScope === "signers-only"
      ? (["signer", "userSigner"] as const)
      : undefined;
  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      isDirty
      hideHeader
      hideCloseButton
      title={`Edit ${displayName}`}
      description={
        showSaveAsCopyButton
          ? "Save a new version of this file or create a separate copy."
          : "Save a new version of this file."
      }
      bodyContained
      className="top-0 left-0 h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
    >
      <div className="relative flex min-h-0 grow flex-col overflow-hidden">
        {isSaving && <FormLoadingOverlay />}
        <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
          {editorFile.isLoading && (
            <p className="text-muted-foreground text-sm">Loading document...</p>
          )}
          {editorFile.isError && (
            <p role="alert" className="text-destructive text-sm">
              Unable to load this document. Please try again.
            </p>
          )}
          <DocxEditor
            key={`docx-editor-${sourceFile.id}`}
            ref={editorRef}
            fileName={displayName}
            initialDocUrl={editorFile.url}
            fileType="firm-file"
            licenseKey={VITE_APRYSE_LICENSE_KEY}
            onTemplateFieldsRequest={handleOpenTemplateFields}
            onReadyChange={setIsEditorReady}
            isActive={open && !!editorFile.url}
            showSaveButton={false}
            showHeader
            headerActions={
              <div className="flex items-center gap-2">
                <TemplateFieldsDialog
                  hideTrigger
                  onInsertAtCursor={handleInsertAtCursor}
                  triggerLabel="Template variables"
                  triggerRef={templateFieldsTriggerRef}
                  allowedParents={allowedTemplateFieldParents}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setConfirmClose(true);
                  }}
                >
                  <XIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            }
            className="min-h-0 flex-1"
          />
        </div>
        {!editorFile.isLoading &&
          !editorFile.isError &&
          !editorFile.canSave && (
            <p className="text-muted-foreground px-6 py-2 text-sm">
              {editorFile.saveUnavailableReason}
            </p>
          )}
        <SheetFooter addSlideoverStyles className="mt-auto shrink-0">
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton setOpen={setOpen} isDirty />
            <Button
              type="button"
              variant={showSaveAsCopyButton ? "outline" : "light-primary"}
              onClick={() => {
                void handleSaveOriginal();
              }}
              disabled={!editorFile.canSave || !isEditorReady || isSaving}
            >
              {saveMode === "original" && isSaving ? "Saving..." : "Save"}
            </Button>
            {showSaveAsCopyButton ? (
              <Button
                type="button"
                variant="light-primary"
                onClick={() => {
                  void handleCreateFromDocx();
                }}
                disabled={!isEditorReady || isSaving}
              >
                {saveMode === "copy" && isSaving ? "Saving..." : "Save as copy"}
              </Button>
            ) : null}
          </div>
        </SheetFooter>
      </div>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        afterActionFn={() => {
          setOpen(false);
          setConfirmClose(false);
        }}
      />
    </AppModal>
  );
};
